/**
 * CSV Import Wizard
 *
 * Fullscreen wizard for importing data:
 *   1. Upload — choose files + name the import (file uploads only; a
 *      source-driven import has no file, so the step does not exist)
 *   2. Mapping — match source columns/fields to object-type fields
 *   3. Review — analyze, resolve conflicts, approve (terminal step,
 *      owns its own buttons)
 *
 * The wizard serves both a file upload and a source-driven import (e.g.
 * Lightfield, which arrives over an API with no file at all). The words it uses
 * about its source — and whether step 1 exists — come from `import-vocabulary`
 * rather than being hardcoded to the CSV case; see that module for why.
 *
 * Wizard chrome (modal shell, step indicator, footer, dirty-close
 * confirmation) is provided by the base `Wizard` component. This file
 * owns the per-step CTA behavior and the unmapped-stage-values
 * confirmation flow.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AlertFloating } from '@/lib/base/overlays/alerts';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import {
  useWizardContext,
  Wizard,
  type WizardCtx,
  type WizardStep,
} from '@/lib/base/wizard';
import { suggestMappings } from '@/lib/csv-import/api';

import {
  countUnmappedStageValues,
  synthesizeSingleTypeSuggestion,
} from './csv-import-helpers';
import { vocabularyForFiles } from './import-vocabulary';
import { ImportVocabularyProvider } from './ImportVocabularyProvider';
import { MappingStep } from './steps/MappingStep';
import { ReviewStep } from './steps/ReviewStep';
import { UploadStep } from './steps/UploadStep';
import { useCsvImport, type UseCsvImportReturn } from './useCsvImport';

// ============================================================================
// Types
// ============================================================================

interface CsvImportWizardProps {
  orgId: number;
  isOpen: boolean;
  onClose: () => void;
  /**
   * When set, the wizard opens at the Mapping step pre-populated with an
   * existing import's data so the user can edit column mappings and
   * resubmit. Used by the ReviewModal's "Edit Mappings" flow.
   */
  resumeImportId?: number | null;
}

const STEP_UPLOAD = 'upload';
const STEP_MAPPING = 'mapping';
const STEP_REVIEW = 'review';

// Wider centered content column for steps that need it (Mapping has the
// sidebar + detail pane; Review renders tables). Upload step keeps the
// wizard's default 672px max-width — too much horizontal whitespace there
// would leave the dropzone and import-name input stranded.
const WIDE_STEP_MAX_CONTENT_WIDTH = 1100;

// ============================================================================
// Step body wrappers — each renders its step's content plus the shared
// inline error banner that lived above the previous slideout's content.
// ============================================================================

interface UploadStepBodyProps {
  orgId: number;
  state: UseCsvImportReturn;
}

function UploadStepBody({ orgId, state }: UploadStepBodyProps) {
  return (
    <>
      {state.error && (
        <div className='mb-4'>
          <AlertFloating
            title='Error'
            description={state.error}
            color='error'
            onClose={state.clearError}
          />
        </div>
      )}
      <UploadStep orgId={orgId} state={state} />
    </>
  );
}

interface MappingStepBodyProps {
  orgId: number;
  state: UseCsvImportReturn;
}

function MappingStepBody({ orgId, state }: MappingStepBodyProps) {
  return (
    <>
      {state.error && (
        <div className='mb-4'>
          <AlertFloating
            title='Error'
            description={state.error}
            color='error'
            onClose={state.clearError}
          />
        </div>
      )}
      <MappingStep
        orgId={orgId}
        uploadedFiles={state.uploadedFiles}
        columnMappings={state.columnMappings}
        onColumnMappingsChange={state.setColumnMappings}
        fileColumns={state.fileColumns}
        suggestedAssociations={state.suggestedAssociations}
        stageMappings={state.stageMappings}
        onStageMappingsChange={state.setStageMappings}
        matchColumns={state.matchColumns}
        onMatchColumnToggle={state.handleMatchColumnToggle}
        multiTypeSuggestions={state.multiTypeSuggestions}
        onNewPropertyFieldTypeChange={state.handleNewPropertyFieldTypeChange}
        associationTypeSelections={state.associationTypeSelections}
        onAssociationTypeSelectionChange={state.setAssociationTypeSelection}
        onCreateProperty={state.handleCreateProperty}
        userNewProperties={state.userNewProperties}
        onUserNewPropertyFieldTypeChange={
          state.handleUserNewPropertyFieldTypeChange
        }
        onUserNewPropertyOptionsChange={
          state.handleUserNewPropertyOptionsChange
        }
        onUserNewPropertyLabelChange={state.handleUserNewPropertyLabelChange}
        onGenerateSelectOptions={state.handleGenerateSelectOptions}
        fileSampleRows={state.fileSampleRows}
        fileObjects={state.fileObjects}
        fileRowCounts={state.fileRowCounts}
        crmEntityAssociationMappings={state.crmEntityAssociationMappings}
        analysisErrorProperties={state.analysisErrorProperties}
        onCrmEntityAssociationMappingsChange={
          state.setCrmEntityAssociationMappings
        }
        fixedValueMappings={state.fixedValueMappings}
        onFixedValueMappingsChange={state.setFixedValueMappings}
        onAddEntityType={state.addEntityTypeToFile}
        associationMappings={state.associationMappings}
        onAssociationMappingsChange={state.setAssociationMappings}
      />
    </>
  );
}

interface ReviewStepBodyProps {
  orgId: number;
  state: UseCsvImportReturn;
}

function ReviewStepBody({ orgId, state }: ReviewStepBodyProps) {
  const { goTo } = useWizardContext();
  const handleEditMappings = useCallback(() => {
    goTo(STEP_MAPPING);
  }, [goTo]);

  return (
    <ReviewStep
      status={state.status}
      importData={state.importData}
      importId={state.importId}
      error={state.error}
      isPolling={state.isPolling}
      orgId={orgId}
      onRetry={state.submit}
      onEditMappings={handleEditMappings}
    />
  );
}

// ============================================================================
// Main component
// ============================================================================

export function CsvImportWizard({
  orgId,
  isOpen,
  onClose,
  resumeImportId,
}: CsvImportWizardProps) {
  const state = useCsvImport(orgId, resumeImportId);
  // Keep a ref current during each render so render delegates can read the
  // latest state without capturing it in their dep arrays — prevents steps
  // useMemo from recomputing on every render just because state changed identity.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Which words this wizard uses about its source, and whether it has an upload
  // step at all. Returns one of two module constants, so the reference is stable
  // across renders and safe to depend on directly.
  const vocabulary = vocabularyForFiles(state.uploadedFiles);

  // Awaitable confirmation flow for the unmapped-stage-values warning.
  // The promise resolver lives in a ref so the dialog's onConfirm/onCancel
  // handlers can resolve the same promise the step 2 onBeforeNext is
  // awaiting.
  const [warningDialog, setWarningDialog] = useState<{
    isOpen: boolean;
    unmappedCount: number;
  }>({ isOpen: false, unmappedCount: 0 });
  const warningResolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const showUnmappedWarning = useCallback(
    (unmappedCount: number) =>
      new Promise<boolean>(resolve => {
        warningResolverRef.current = resolve;
        setWarningDialog({ isOpen: true, unmappedCount });
      }),
    []
  );

  const handleWarningConfirm = useCallback(() => {
    setWarningDialog(prev => ({ ...prev, isOpen: false }));
    warningResolverRef.current?.(true);
    warningResolverRef.current = null;
  }, []);

  const handleWarningCancel = useCallback(() => {
    setWarningDialog(prev => ({ ...prev, isOpen: false }));
    warningResolverRef.current?.(false);
    warningResolverRef.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // Per-step onBeforeNext logic
  // ---------------------------------------------------------------------------

  // Destructure specific state fields used in callbacks so dependency arrays
  // don't reference the entire `state` object (which is a new identity each
  // render, defeating useCallback memoization).
  const {
    dirtyTypes,
    uploadedFiles: stateUploadedFiles,
    fileColumns: stateFileColumns,
    fileSampleRows: stateFileSampleRows,
    handleMultiTypeSuggestionAccepted,
    seedMatchColumns,
    setDirtyTypes,
    setStageMappings,
    setUploadedFiles,
    stageMappings,
    submit,
    reset,
  } = state;

  const handleUploadBeforeNext = useCallback(async () => {
    // Re-suggest mappings for files whose type was changed since last
    // visit. Errors here are non-fatal — we surface a toast and continue
    // (matches the previous behavior). The wizard owns the
    // `isSubmitting` flag for the duration of this promise, which is
    // what drives the "Analyzing" label on the primary CTA.
    if (dirtyTypes.size === 0) {
      return;
    }
    try {
      for (const fileId of dirtyTypes) {
        const fileIdStr = String(fileId);
        const matchingFile = stateUploadedFiles.find(
          f => String(f.file_id) === fileIdStr
        );
        const headers = stateFileColumns[fileIdStr];
        if (!matchingFile || !headers) {
          continue;
        }
        try {
          const sampleRows = stateFileSampleRows[fileIdStr] ?? undefined;
          const suggestions = await suggestMappings(
            orgId,
            headers,
            matchingFile.entity_type_name,
            sampleRows
          );
          handleMultiTypeSuggestionAccepted(
            fileId,
            synthesizeSingleTypeSuggestion(
              matchingFile.entity_type_name,
              suggestions.mappings,
              suggestions.new_properties,
              suggestions.internal_columns
            )
          );
          const editKey = `${fileId}_virtual_${matchingFile.entity_type_name}`;
          // Re-apply stage-column auto-mapping under the editKey.
          // handleMultiTypeSuggestionAccepted drops all stage mappings for this
          // file's virtual keys, so the "no stage_column → clear" branch is
          // already handled implicitly.
          if (suggestions.stage_column) {
            setStageMappings(prev => ({
              ...prev,
              [editKey]: {
                column: suggestions.stage_column as string,
                value_mappings: {},
              },
            }));
          }
          // Pre-select suggested ID-like columns as match columns. Seeding is
          // additive-only (never clears an existing selection) — the mapping
          // eligibility self-heal in FileMappingSection removes any column
          // that isn't actually a valid match candidate.
          seedMatchColumns(editKey, suggestions.suggested_match_columns ?? []);
          // Attach new property suggestions to the file entry
          if (
            suggestions.new_properties &&
            suggestions.new_properties.length > 0
          ) {
            setUploadedFiles(prev =>
              prev.map(f =>
                String(f.file_id) === fileIdStr
                  ? { ...f, new_properties: suggestions.new_properties }
                  : f
              )
            );
          }
        } catch {
          toast.error(
            "Couldn't refresh column suggestions – please review mappings manually."
          );
        }
      }
    } finally {
      setDirtyTypes(new Set());
    }
  }, [
    orgId,
    dirtyTypes,
    stateUploadedFiles,
    stateFileColumns,
    stateFileSampleRows,
    handleMultiTypeSuggestionAccepted,
    seedMatchColumns,
    setDirtyTypes,
    setStageMappings,
    setUploadedFiles,
  ]);

  const handleMappingBeforeNext = useCallback(async () => {
    // If any stage values are unmapped, surface the warning dialog and
    // wait for the user's decision. They can either proceed (default
    // stage for unmapped rows) or cancel and fix the mappings.
    const unmappedCount = countUnmappedStageValues(stageMappings);
    if (unmappedCount > 0) {
      const proceed = await showUnmappedWarning(unmappedCount);
      if (!proceed) {
        throw new Error('Cancelled by user');
      }
    }
    // submit() throws on failure — re-raise so the wizard keeps the user
    // on step 2 (which still has its footer + retry path).
    await submit();
  }, [stageMappings, submit, showUnmappedWarning]);

  // ---------------------------------------------------------------------------
  // Close handler — reset state on close so reopening starts fresh
  // ---------------------------------------------------------------------------

  const handleClose = useCallback(() => {
    // Tear down any in-flight unmapped-values warning so the overlay can't
    // outlive the wizard (and leave the resolver promise dangling).
    setWarningDialog({ isOpen: false, unmappedCount: 0 });
    warningResolverRef.current?.(false);
    warningResolverRef.current = null;
    reset();
    onClose();
  }, [reset, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  // ---------------------------------------------------------------------------
  // Step config
  // ---------------------------------------------------------------------------

  // Validation for step 1: at least one file, a non-empty name, and no
  // in-flight uploads. Blocking on the upload counter prevents the wizard
  // from advancing into the Mapping step before headers/file_ids exist.
  const isUploadValid =
    state.uploadedFiles.length > 0 &&
    state.importName.trim().length > 0 &&
    state.uploadingFileCount === 0;
  // Validation for step 2: hook computes this
  const { isMappingValid } = state;
  // Terminal import state — show the wizard footer with a Done button
  // once the import has completed or failed.
  const isImportTerminal =
    state.status === 'completed' || state.status === 'failed';

  // Stable render delegates — use stateRef.current so they don't capture
  // `state` directly. This keeps the steps useMemo stable across renders
  // where only state identity changes (not the values that drive canAdvance).
  const renderUploadStep = useCallback(
    () => <UploadStepBody orgId={orgId} state={stateRef.current} />,
    [orgId]
  );

  const renderMappingStep = useCallback(
    () => <MappingStepBody orgId={orgId} state={stateRef.current} />,
    [orgId]
  );

  const renderReviewStep = useCallback(
    () => <ReviewStepBody orgId={orgId} state={stateRef.current} />,
    [orgId]
  );

  const steps = useMemo<WizardStep[]>(
    () => [
      // A source-driven import has no upload: the user connected the source in
      // its own wizard and there is no file to choose. Rendering the step would
      // show "Upload ✓" in the stepper and leave a file-drop screen one Back
      // click away for an import that can never have a file.
      ...(vocabulary.hasUploadStep
        ? [
            {
              id: STEP_UPLOAD,
              label: 'Upload',
              title: 'Upload your files',
              subtitle:
                'Add one or more CSV or Excel files. We’ll auto-detect the object type and column mappings.',
              render: renderUploadStep,
              canAdvance: () => isUploadValid,
              onBeforeNext: handleUploadBeforeNext,
              primaryCta: {
                label: (ctx: WizardCtx) =>
                  ctx.isSubmitting ? 'Analyzing' : 'Next',
              },
              secondaryCtas: [
                {
                  id: 'cancel',
                  label: 'Cancel',
                  onClick: ({ close }: WizardCtx) => close(),
                },
              ],
            },
          ]
        : []),
      {
        id: STEP_MAPPING,
        label: 'Mapping',
        title: vocabulary.mappingTitle,
        subtitle: vocabulary.mappingSubtitle,
        render: renderMappingStep,
        canAdvance: () => isMappingValid,
        onBeforeNext: handleMappingBeforeNext,
        // The Mapping step has a sidebar + detail-pane layout. fillHeight
        // hands it the full content area so the sidebar stays pinned and
        // only the detail pane scrolls (vs. the whole step scrolling under
        // the outer wizard scroll container).
        fillHeight: true,
        maxContentWidth: WIDE_STEP_MAX_CONTENT_WIDTH,
        primaryCta: {
          label: ctx => (ctx.isSubmitting ? 'Processing' : 'Next'),
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
      },
      {
        id: STEP_REVIEW,
        label: 'Review',
        // ReviewStep owns its own internal layout (summary / conflicts /
        // progress sub-states) and renders its own action buttons for
        // most sub-states. Show the wizard footer only when the import
        // reaches a terminal state so the Done/Close button sits in the
        // standard footer position.
        hideFooter: !isImportTerminal,
        hideBackButton: true,
        // Review renders horizontally-scrolling data tables — give it the
        // wider content column so they breathe.
        maxContentWidth: WIDE_STEP_MAX_CONTENT_WIDTH,
        render: renderReviewStep,
        primaryCta: isImportTerminal
          ? {
              label: 'Done',
              onClick: ({ close }) => close(),
            }
          : 'auto',
      },
    ],
    [
      vocabulary,
      isUploadValid,
      isMappingValid,
      isImportTerminal,
      handleUploadBeforeNext,
      handleMappingBeforeNext,
      renderUploadStep,
      renderMappingStep,
      renderReviewStep,
    ]
  );

  return (
    <ImportVocabularyProvider files={state.uploadedFiles}>
      <Wizard
        /*
         * Remount when the step topology changes. In resume mode the import
         * loads *after* mount, so the wizard first renders file-shaped
         * ([Upload, Mapping, Review]) and only then learns it is source-driven
         * ([Mapping, Review]). Wizard holds the current step as an INDEX in
         * state, seeded once at mount — dropping Upload shifts every index
         * under that pointer, so `defaultStepId: 'mapping'` (index 1) silently
         * became Review (now index 1). Keying on the topology re-seeds the
         * index against the correct array. The flip happens once, immediately
         * after load and before the user has touched anything, so nothing is
         * lost. (The underlying index-vs-dynamic-steps fragility is a base
         * Wizard issue affecting any consumer with a changing steps array.)
         */
        key={vocabulary.hasUploadStep ? 'with-upload' : 'no-upload'}
        mode='fullscreen'
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        steps={steps}
        dialogAriaLabel={
          vocabulary.hasUploadStep ? 'Import files' : 'Import records'
        }
        instance='csv-import'
        defaultStepId={resumeImportId ? STEP_MAPPING : undefined}
      />
      <ConfirmationDialog
        isOpen={warningDialog.isOpen}
        title='Unmapped stage values'
        description={`${warningDialog.unmappedCount} row${warningDialog.unmappedCount !== 1 ? 's' : ''} with unmapped stage values will get the default stage if you set one, otherwise no stage. Continue anyway?`}
        confirmLabel='Import anyway'
        cancelLabel='Go back'
        variant='warning'
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
        instance='unmapped-stage-warning'
      />
    </ImportVocabularyProvider>
  );
}

export default CsvImportWizard;
